import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Badge, Box, Text } from "@mantine/core";
import type { Segment } from "../api/types";
import { usePlayer } from "../state/player";
import { formatDuration } from "../time/format";
import { msToZonedText } from "../time/parse";
import { Chip } from "../ui/Chip";

/** One row of the facts grid: a dimmed term, then its definition. */
export function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <Box component="dt" c="dimmed">
        {label}
      </Box>
      <Box component="dd" m={0} style={{ overflowWrap: "anywhere" }}>
        {children}
      </Box>
    </>
  );
}

/** How much of the recording was taken out of the middle, when any was. */
export function ExcisionsNote({ segment }: { segment: Segment }) {
  const { t } = useTranslation("admin");
  if (segment.excisions.length === 0) return null;
  return (
    <Text size="xs" c="dimmed" mb={10}>
      {t("inspector.excised", {
        duration: formatDuration(segment.excisions.reduce((total, gap) => total + gap.seconds, 0)),
      })}
    </Text>
  );
}

/** What the panel knows about a row but does not edit: detection, matching, audio. */
export function FactsGrid({ segment, zone }: { segment: Segment; zone: string }) {
  const { t } = useTranslation("admin");
  return (
    <Box
      component="dl"
      fz={12}
      pt={12}
      style={{
        display: "grid",
        gridTemplateColumns: "auto minmax(0, 1fr)",
        gap: "4px 12px",
        margin: "14px 0 0",
        borderTop: "1px solid var(--mantine-color-default-border)",
      }}
    >
      <Fact label={t("inspector.factDetected")}>
        {msToZonedText(Date.parse(segment.detected_start), zone)} →{" "}
        {msToZonedText(Date.parse(segment.detected_end), zone)}
      </Fact>
      <Fact label={t("inspector.factRawTitle")}>{segment.raw_title ?? "—"}</Fact>
      <Fact label={t("inspector.factEvent")}>
        {segment.event_id === null
          ? t("inspector.eventUnlinked")
          : segment.match_score === null
            ? `#${segment.event_id}`
            : t("inspector.eventScored", {
                id: segment.event_id,
                score: segment.match_score.toFixed(2),
              })}
      </Fact>
      {segment.matched_in_id !== null && (
        <Fact label={t("inspector.factMatchedIn")}>
          <Chip
            title={t("inspector.matchedInHint")}
            onClick={() => usePlayer.getState().select(segment.matched_in_id)}
          >
            {t("inspector.matchedInShow", { id: segment.matched_in_id })}
          </Chip>
        </Fact>
      )}
      <Fact label={t("inspector.factAudio")}>
        {t(`inspector.audioState.${segment.audio_state}`, { defaultValue: segment.audio_state })}
      </Fact>
    </Box>
  );
}

// The flag marks a state rather than labelling a thing, and the theme's `Badge` defaults are
// the rounded light pill `StatePill` wears -- so the square uppercase mark is asked for here.
const FLAG = {
  size: "sm",
  radius: "sm",
  tt: "uppercase",
  lts: "0.06em",
  variant: "filled",
} as const;

export function StateBadge({ segment }: { segment: Segment }) {
  const { t } = useTranslation("admin");
  // A running show is provisional, but calling it unverified in the one panel an admin acts
  // from invites the edit that would lock its end before the show has one.
  if (segment.on_air) {
    return (
      <Badge {...FLAG} color="danger">
        {t("inspector.onAir")}
      </Badge>
    );
  }
  // A matched track was never in review's sense of unverified. The fingerprinter put it on
  // the timeline and the score is the claim; there is nothing here for a person to verify.
  // Its own branch because `matched` is not a review state, so the ink below has no arm for it.
  if (segment.matched_in_id !== null) {
    return (
      <Badge {...FLAG} color="sunken" c="var(--mantine-color-text)">
        {t("inspector.matched")}
      </Badge>
    );
  }
  // A jukebox track is not unreviewed work -- nobody is ever going to verify it, and the
  // queue does not ask them to. It is what it is, so the badge says so.
  const label =
    segment.state === "provisional"
      ? segment.auto_approved
        ? t("inspector.autoApproved")
        : segment.kind === "track"
          ? t("inspector.jukebox")
          : t("inspector.unverified")
      : t(`inspector.segmentState.${segment.state}`, { defaultValue: segment.state });
  // Ink only, on the fill every other flag wears: the review states are told apart by the
  // colour of the word, and tinting the chip behind it would be a new distinction.
  const ink =
    segment.state === "approved"
      ? "accent"
      : segment.state === "exported"
        ? "jukebox"
        : "var(--mantine-color-text)";
  return (
    <Badge {...FLAG} color="sunken" c={ink}>
      {label}
    </Badge>
  );
}
